import { useEffect, useRef, useState, useCallback } from "react";
import {
  getAccesparkCommerces,
  getEzytecCommerces,
  getAccesparkTerminals,
  submitAccesparkCycle,
} from "modules/monitoreo/services/ciclosAccespark";
import { bogotaNow, normalizePlate } from "../../creacionCiclo/model/cycleModel";
import { accesparkEntry, terminalDirection } from "../model/accesparkModel";
const initial = () => ({ commerce: "", terminal: "", plate: "", ...bogotaNow() });
export default function useAccesparkForm(onComplete, system = "accespark") {
  const [values, setValues] = useState(initial);
  const [commerces, setCommerces] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(false);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [commerceError, setCommerceError] = useState(null);
  const [terminalError, setTerminalError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(false),
    commerceRequest = useRef(null),
    terminalRevision = useRef(0),
    locked = useRef(false),
    currentValues = useRef(values);
  currentValues.current = values;
  const loadCommerces = useCallback(async () => {
    if (commerceRequest.current) return commerceRequest.current;
    setLoadingCommerces(true);
    setCommerceError(null);
    commerceRequest.current = (async () => {
      try {
        const list = await (system === "ezytec" ? getEzytecCommerces() : getAccesparkCommerces());
        if (mounted.current) setCommerces(list);
      } catch (error) {
        if (mounted.current) setCommerceError(error.message);
      } finally {
        commerceRequest.current = null;
        if (mounted.current) setLoadingCommerces(false);
      }
    })();
    return commerceRequest.current;
  }, [system]);
  useEffect(() => {
    mounted.current = true;
    loadCommerces();
    return () => {
      mounted.current = false;
      terminalRevision.current++;
    };
  }, [loadCommerces]);
  const loadTerminals = useCallback(async (commerceId) => {
    const revision = ++terminalRevision.current;
    setTerminals([]);
    setTerminalError(null);
    setLoadingTerminals(Boolean(commerceId));
    if (!commerceId) return;
    try {
      const list = await getAccesparkTerminals(commerceId);
      if (mounted.current && revision === terminalRevision.current) setTerminals(list);
    } catch (error) {
      if (mounted.current && revision === terminalRevision.current) setTerminalError(error.message);
    } finally {
      if (mounted.current && revision === terminalRevision.current) setLoadingTerminals(false);
    }
  }, []);
  const setField = (field, value) => {
    if (locked.current) return;
    const next = {
      ...currentValues.current,
      [field]:
        field === "plate"
          ? normalizePlate(value)
              .replace(/[^A-Z0-9]/g, "")
              .slice(0, 6)
          : value,
    };
    if (field === "commerce") {
      next.terminal = "";
      loadTerminals(value);
    }
    currentValues.current = next;
    setValues(next);
    setError(null);
  };
  const commerce = commerces.find((item) => item.id === values.commerce) || null;
  const terminal =
    terminals.find((item) => item.id === values.terminal && item.commerceId === values.commerce) ||
    null;
  const validate = () => {
    if (!commerce) return "Seleccione un comercio";
    if (loadingTerminals || !terminal) return "Seleccione una terminal del comercio actual";
    if (!terminalDirection(terminal.type)) return "La terminal no tiene tipo Entrada o Salida";
    if (!/^[A-Z]{3}\d{3}$/.test(values.plate)) return "Ingrese una placa como ABC123";
    try {
      accesparkEntry(values.date, values.time);
    } catch (error) {
      return error.message;
    }
    const instant = new Date(`${values.date}T${values.time}:00-05:00`);
    return instant.getTime() > Date.now() ? "La fecha y hora no pueden ser futuras" : null;
  };
  const submit = async () => {
    if (locked.current) return { success: false };
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return { success: false };
    }
    locked.current = true;
    setBusy(true);
    setError(null);
    let result;
    try {
      result = await submitAccesparkCycle({
        commerce,
        terminal,
        plate: values.plate,
        date: values.date,
        time: values.time,
      });
      if (mounted.current) {
        if (result.success) {
          const next = { ...values, plate: "", ...bogotaNow() };
          currentValues.current = next;
          setValues(next);
        } else setError(result.message);
      }
    } catch (error) {
      result = { success: false };
      if (mounted.current) setError(error.message);
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
    if (mounted.current && onComplete) {
      try {
        await onComplete(result);
      } catch {
        /* El historial puede reintentarse sin repetir la operación. */
      }
    }
    return result;
  };
  return {
    values,
    setField,
    commerces,
    terminals,
    commerce,
    terminal,
    loadingCommerces,
    loadingTerminals,
    commerceError,
    terminalError,
    loadCommerces,
    retryTerminals: () => loadTerminals(values.commerce),
    busy,
    error,
    setError,
    validate,
    submit,
  };
}
